import React, { useState, useEffect, useCallback } from 'react';
import {
    GitMerge, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    Loader2, RefreshCw, ChevronDown, ChevronUp
} from 'lucide-react';
import { dataQualityService } from '../../services/dataQualityService';
import CustomSelect from '../shared/CustomSelect';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { ErrorAlert } from '../shared/ErrorAlert';
import { FormatUtils } from '../shared/FormatUtils';

const MergeHistory = () => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [paginating, setPaginating] = useState(false);
    const [error, setError] = useState('');
    const [expandedRow, setExpandedRow] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const fetchHistory = useCallback(async () => {
        try {
            setPaginating(true);
            setError('');
            const response = await dataQualityService.merge.history(currentPage, pageSize);

            if (response.success && response.data) {
                const pageData = response.data;
                setHistory(pageData.content || []);
                setTotalElements(pageData.totalElements || 0);
                setTotalPages(pageData.totalPages || 0);
            }
        } catch (err) {
            console.error('Error fetching merge history:', err);
            setError('Failed to load merge history');
        } finally {
            setLoading(false);
            setPaginating(false);
        }
    }, [currentPage, pageSize]);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    const parseJsonSafe = (str) => {
        try { return JSON.parse(str); }
        catch { return str; }
    };

    if (loading) {
        return <LoadingSpinner message="Loading merge history..." />;
    }

    const startIndex = Math.min(currentPage * pageSize + 1, totalElements);
    const endIndex = Math.min((currentPage + 1) * pageSize, totalElements);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-semibold text-gray-900">Merge History</h2>
                    <p className="text-gray-600 mt-1">{totalElements.toLocaleString()} merge operations</p>
                </div>
                <button
                    onClick={fetchHistory}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition flex items-center gap-2"
                >
                    <RefreshCw className="w-4 h-4" />
                    Refresh
                </button>
            </div>

            {error && <ErrorAlert error={error} onRetry={fetchHistory} />}

            {/* History Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden relative">
                {paginating && (
                    <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    </div>
                )}

                {history.length === 0 ? (
                    <div className="text-center py-12">
                        <GitMerge className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No merge history yet</p>
                    </div>
                ) : (
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8"></th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Primary Client</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Merged Clients</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Performed By</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                            {history.map((entry) => (
                                <React.Fragment key={entry.id}>
                                    <tr
                                        className="hover:bg-gray-50 transition cursor-pointer"
                                        onClick={() => setExpandedRow(expandedRow === entry.id ? null : entry.id)}
                                    >
                                        <td className="px-6 py-4 text-gray-400">
                                            {expandedRow === entry.id
                                                ? <ChevronUp className="w-4 h-4" />
                                                : <ChevronDown className="w-4 h-4" />
                                            }
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {FormatUtils.formatDate(entry.performedAt, 'long')}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {entry.primaryClientCode}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex gap-1 flex-wrap">
                                                {(parseJsonSafe(entry.mergedClientCodes) || []).map((code, idx) => (
                                                    <span key={idx} className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700">
                                                        {typeof code === 'string' ? code : JSON.stringify(code)}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {entry.performedBy}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                                            {entry.reason || '-'}
                                        </td>
                                    </tr>
                                    {expandedRow === entry.id && (
                                        <tr>
                                            <td colSpan={6} className="px-6 py-4 bg-gray-50">
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-700 mb-2">Reassignment Summary</p>
                                                        <pre className="text-xs bg-white p-3 rounded border border-gray-200 overflow-x-auto">
                                                            {JSON.stringify(parseJsonSafe(entry.reassignmentSummary), null, 2)}
                                                        </pre>
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-700 mb-2">Field Conflict Resolutions</p>
                                                        <pre className="text-xs bg-white p-3 rounded border border-gray-200 overflow-x-auto">
                                                            {JSON.stringify(parseJsonSafe(entry.fieldConflictResolutions), null, 2)}
                                                        </pre>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                )}

                {/* Pagination */}
                {(totalPages > 0 || totalElements > 0) && (
                    <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
                        <div className="flex items-center gap-4">
                            <span className="text-sm text-gray-700">
                                Showing <span className="font-medium">{startIndex.toLocaleString()}</span> to{' '}
                                <span className="font-medium">{endIndex.toLocaleString()}</span> of{' '}
                                <span className="font-medium">{totalElements.toLocaleString()}</span> results
                            </span>
                            <CustomSelect
                                value={pageSize}
                                onChange={(val) => { setPageSize(Number(val)); setCurrentPage(0); }}
                                options={[
                                    { value: 10, label: '10 per page' },
                                    { value: 20, label: '20 per page' },
                                    { value: 50, label: '50 per page' },
                                ]}
                                dropUp
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={() => setCurrentPage(0)} disabled={currentPage === 0 || paginating} className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition">
                                <ChevronsLeft className="w-5 h-5" />
                            </button>
                            <button onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 0 || paginating} className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition">
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <span className="px-4 py-2 text-sm text-gray-700">
                                Page {(currentPage + 1).toLocaleString()} of {totalPages.toLocaleString() || 1}
                            </span>
                            <button onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage >= totalPages - 1 || paginating} className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition">
                                <ChevronRight className="w-5 h-5" />
                            </button>
                            <button onClick={() => setCurrentPage(totalPages - 1)} disabled={currentPage >= totalPages - 1 || paginating} className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition">
                                <ChevronsRight className="w-5 h-5" />
                            </button>
                            {paginating && <Loader2 className="w-5 h-5 animate-spin text-blue-600 ml-2" />}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MergeHistory;
